import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import externalAxios from "axios";
import { axios } from "../../lib/request";
import { useState } from 'react'

const readFileURL = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = ({ target }) => resolve(target.result);
        reader.readAsDataURL(file);
    });
};

export const createNub = async (game_id, { media_url, ...params }) => {
    const url = `${process.env.REACT_APP_API_NUBS_URL}`;
    return await axios.post(url, { ...params, game_id });
};

export const updateNub = async (game_id, nub) => {
    const { id, media_url, ...params } = nub
    const url = `${process.env.REACT_APP_API_NUBS_UPDATE_URL}/${id}.json`;
    return await axios.put(url, { ...params, game_id });
};

export const useReorderAfterDelete = ({ taskId, useQuery }) => {
    const [collection, setCollection] = useQuery()
    const { mutateAsync: _updateNub } = useUpdateNubsMutation({ taskId })
    const reOrder = async (deletedNub, additionalPayload = {}) => {
        const sourceList = [
            ...collection.nubs,
        ].filter(
            n => parseInt(n.id) !== parseInt(deletedNub.id)
        )
        const list = sourceList.map((l, index) => ({
            ...l,
            // position starts from 1 (non zero index)
            position: index + 1,
        }))
        const promises = list.map(async (l) => {
            await _updateNub({
                id: l.id,
                position: l.position,
            })
        })
        await Promise.all(promises)
        await setCollection({
            ...collection,
            nubs: [...list],
            ...additionalPayload,
        })
    }
    return reOrder
}

const useReorderAfterCreate = ({ taskId, useQuery }) => {
    const [collection, setCollection] = useQuery()
    const { mutateAsync: _updateNub } = useUpdateNubsMutation({ taskId })
    const reOrder = async (addedNub, additionalPayload = {}) => {
        const sourceList = [
            addedNub,
            ...collection.nubs,
        ]
        const list = sourceList.map((l, index) => ({
            ...l,
            // position starts from 1 (non zero index)
            position: index + 1,
        }))
        const promises = list.map(async (l) => {
            await _updateNub({
                id: l.id,
                position: l.position,
            })
        })
        await Promise.all(promises)
        await setCollection({
            ...collection,
            nubs: [...list],
            ...additionalPayload,
        })
    }
    return reOrder
}

export const useCreateAiPhotoMutation = ({ taskId, useQuery, mutationOptions = {} }) => {
    const { game_id } = useParams()
    const reOrder = useReorderAfterCreate({ taskId, useQuery })

    /**
     * saving AI photo takes a different workflow than the regular nubs creation.
     * It will first try to check if the API does hit its limit yet.
     * It uses createNub endpoint from the setting state, and conditionally add task_id for challenges' nubs.
     * Upon successfull AI creation, it will be inserted into the store collection.
     */
    const { mutateAsync, isLoading } = useMutation(
        async (prompt) => {
            const response = await createNub(game_id, {
                position: 1,
                prompt,
                generate_type: 1,
                task_id: taskId,
            })
            const { data } = response || {}
            if (data?.nub) {
                reOrder(data.nub)
            }
            return data
        },
        {
            ...mutationOptions,
        }
    );
    return {
        loading: isLoading,
        createAiPhoto: mutateAsync,
    };
};

export const useUploadNubsMutation = ({ taskId, useQuery }) => {
    const { game_id } = useParams()
    const [collection] = useQuery()
    const [totalSize, setTotalSize] = useState(0)
    const [uploadedSize, setUploadedSize] = useState(0)
    const [fileName, setFileName] = useState('')
    const reOrder = useReorderAfterCreate({ taskId, useQuery })
    const { isLoading, mutateAsync } = useMutation(async (file) => {
        const fileURL = await readFileURL(file);
        const [ext] = file.name.split(".").reverse();
        const url = `${process.env.REACT_APP_API_SIGN_URL
            }/${ext.toLocaleLowerCase()}`;
        const response = await axios.get(url);
        const { data } = response || {}
        const options = { headers: { "Content-Type": file.type } };
        const [uuid, extension] = data.name.split(".");
        const videos = [...collection?.assets?.videos || []]
        const mediaUrl = data?.uri || fileURL
        if (extension.match(/(?:avi|mkv|mov|mp4|webm)$/g)) {
            videos.push({
                name: uuid,
                urls: {
                    mp4: mediaUrl,
                    png: `https://ss-assets.socialscavenger.com/images/${uuid}-00001.png`,
                }
            })
        }

        const newNub = {
            position: 1,
            [`media_uuid`]: uuid,
            [`media_extension`]: extension.toLocaleLowerCase(),
            [`media_url`]: mediaUrl,
        }
        const created = await createNub(game_id, {
            ...newNub,
            task_id: taskId,
        })
        if (created?.data?.nub) {
            await reOrder({
                ...created.data.nub,
                ...newNub,
                media_url_raw: created?.data?.nub?.media_url,
            }, {
                assets: {
                    videos,
                }
            })
        }            

        externalAxios.defaults.onUploadProgress = evt => {
            if (evt.lengthComputable) {
                setTotalSize(evt.total)
                setUploadedSize(evt.loaded)
                setFileName(file.name)
            }
        }
        return await externalAxios.put(data.signature, file, options);
    });
    return { 
        isLoading, 
        mutateAsync, 
        progress: {
            total: totalSize,
            loaded: uploadedSize,
            fileName,
        }
    }
}

export const useCreateChunkMutation = ({ taskId, useQuery }) => {
    const { game_id } = useParams()
    const reOrder = useReorderAfterCreate({ taskId, useQuery })
    const { isLoading, mutateAsync } = useMutation(async (chunk) => {
        const newNub = {
            position: 1,
            chunk,
        }
        const created = await createNub(game_id, {
            ...newNub,
            task_id: taskId,
        })
        if (created?.data?.nub) {
            await reOrder(created.data.nub);
        }
    });
    return { isLoading, mutateAsync }
}

export const useUpdateChunkMutation = ({ taskId, useQuery }) => {
    const { game_id } = useParams()
    const [collection, setCollection] = useQuery()
    const { isLoading, mutateAsync } = useMutation(async ({chunk, id}) => {
        const nub = {
            id,
            chunk,
        }
        try {
            await updateNub(game_id, {
                ...nub,
                task_id: taskId,
            })
            await setCollection({
                ...collection,
                nubs: [
                    ...collection.nubs.map(nubItem => {
                        if (parseInt(nubItem.id) === parseInt(id)) {
                            return {
                                ...nubItem,
                                ...nub,
                            }
                        }
                        return nubItem
                    })
                ],
            });
        } catch {}
    });
    return { isLoading, mutateAsync }
}

export const useUpdateNubsMutation = ({ taskId }) => {
    const { game_id } = useParams()
    const { isLoading, mutateAsync } = useMutation((nubUpdate) => {
        updateNub(parseInt(game_id), {
            ...nubUpdate,
            task_id: taskId,
        })
    })
    return { isLoading, mutateAsync }
}